import { Fragment, useState, useEffect, useMemo, useCallback, useId } from 'react';
import { flushSync } from 'react-dom';
import { ArrowDown, ArrowUp, ChevronDown, Download, Printer, X } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Tag from '../ui/Tag';
import Icon from '../ui/Icon';
import Table from '../ui/Table';
import Select from '../ui/Select';
import DatePicker from '../ui/DatePicker';
import Skeleton from '../ui/Skeleton';
import ErrorState from '../ui/ErrorState';
import EmptyState from '../ui/EmptyState';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';
import OutputBody, { OutputFilters, useOutput, useOutputRange, personOptions, periodLabel, downloadOutputCsv } from './reports/Output';

// Reports on src/ui (DESIGN rev 2.1 §3.1: Table + plain sections). Same five calls, same filters, same numbers and
// the same CSV files as before. Print all prints the page; Print on a section prints that section alone.
// OUT-2 (DESIGN rev 2.3 §3.1): Output is the first section, open by default (pages/reports/Output.jsx). It reads only
// /api/output/*; the filter row gains its range segment and person, and Custom uses the row's from / to dates.

const QUAL_STATUSES = ['I/P', 'Needs Review', 'Needs Revision', 'Approved', 'Backburner'];
const LIVE_STATUSES = ['Scripted', 'Filming', 'Filmed', 'Editing', 'Qual', 'Approved', 'Incomplete', 'Posted'];
const AI_STATUSES   = ['Scripted', 'Editing', 'Qual', 'Approved', 'Posted'];

// A script's qual state as the system words it (§2.8), and the role its bar is drawn in.
const QUAL_LABEL = { 'I/P': 'In progress', 'Needs Review': 'Needs review', 'Needs Revision': 'Needs revision', Approved: 'Approved', Backburner: 'Backburner' };
const QUAL_TONE  = { 'I/P': 'info', 'Needs Review': 'warn', 'Needs Revision': 'warn', Approved: 'ok', Backburner: 'muted' };
// A production status is a stage: its bar steps up the ladder (rung 1..8); Incomplete is a condition.
const STAGE_RUNG = { Writing: 1, Scripted: 2, Filming: 3, Filmed: 4, Editing: 5, Qual: 6, Approved: 7, Posted: 8 };

const DASH = '—'; // the old placeholder, kept in row values so sort order and CSV files are unchanged

// ── Helpers ──────────────────────────────────────────────────────────────────

function thisMonth(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth();
}

function inDateRange(dateStr, start, end) {
  if (!dateStr) return false;
  const d = new Date(dateStr).getTime();
  if (start && d < new Date(start).getTime()) return false;
  if (end   && d > new Date(end + 'T23:59:59').getTime()) return false;
  return true;
}

function buildCounts(list, key, statuses) {
  const counts = Object.fromEntries(statuses.map(s => [s, 0]));
  list.forEach(item => {
    const v = item[key];
    if (counts[v] !== undefined) counts[v]++;
  });
  return counts;
}

function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(','), ...rows.map(r => headers.map(k => esc(r[k])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function applySortRows(rows, sort) {
  if (!sort.col) return rows;
  return [...rows].sort((a, b) => {
    const av = a[sort.col] ?? '';
    const bv = b[sort.col] ?? '';
    const cmp = typeof av === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv));
    return sort.dir === 'asc' ? cmp : -cmp;
  });
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// Print one section: mark it, flag the document, print, unflag. page.css hides every other printable section.
function printOne(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const root = document.documentElement;
  el.setAttribute('data-print-target', '');
  root.setAttribute('data-print-one', '');
  const done = () => {
    el.removeAttribute('data-print-target');
    root.removeAttribute('data-print-one');
    window.removeEventListener('afterprint', done);
  };
  window.addEventListener('afterprint', done);
  window.print();
}

// ── Pieces ───────────────────────────────────────────────────────────────────

// A value the old page showed as a dash: muted "None" on screen, the dash stays in the row for sort and CSV.
const orNone = v => (v == null || v === DASH || v === '' ? <span className="ui-text-3">None</span> : clean(v));

function QualStatus({ value }) {
  if (value === 'I/P' || value === 'Needs Review' || value === 'Needs Revision') return <Badge condition={QUAL_LABEL[value]} />;
  if (value === 'Approved') return <Badge tone="ok">Approved</Badge>;
  if (value === 'Backburner') return <span className="ui-text-3">Backburner</span>;
  return orNone(value);
}

function SortHead({ col, label, sort, setSort }) {
  const active = sort.col === col;
  return (
    <button type="button" className="ui-sort" aria-label={active ? `${label}, sorted ${sort.dir === 'asc' ? 'ascending' : 'descending'}` : `Sort by ${label}`}
      onClick={() => setSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
      {label}
      {active && <Icon icon={sort.dir === 'desc' ? ArrowDown : ArrowUp} />}
    </button>
  );
}

// Columns for a sortable Table: [{ key, label, align?, width?, render? }] → Table columns with SortHead labels.
const sortable = (cols, sort, setSort) => cols.map(c => ({ ...c, label: <SortHead col={c.key} label={c.label} sort={sort} setSort={setSort} /> }));

function Bars({ statuses, counts, labelOf, toneOf }) {
  const max = Math.max(...Object.values(counts), 1);
  return (
    <ul className="ui-bars">
      {statuses.map(status => {
        const count = counts[status] || 0;
        const pct   = Math.round((count / max) * 100);
        const { tone, rung } = toneOf(status);
        return (
          <li key={status} className="ui-bar">
            <span className="ui-bar__label">{labelOf(status)}</span>
            <span className="ui-bar__track" aria-hidden="true">
              <span className="ui-bar__fill" data-tone={tone} data-rung={rung} style={{ width: `${pct}%` }} />
            </span>
            <span className="ui-bar__count">{count}</span>
          </li>
        );
      })}
    </ul>
  );
}
const qualTone  = s => ({ tone: QUAL_TONE[s] });
const stageTone = s => (s === 'Incomplete' ? { tone: 'danger' } : { rung: STAGE_RUNG[s] });
const qualLabel = s => QUAL_LABEL[s] || s;
const stageLabel = s => s;

// A report section: heading that collapses it, a count line, CSV and Print. Collapsed sections print as their
// heading only (as before), except that Print on a collapsed section opens it first.
function Section({ id, title, meta, defaultOpen = true, onExport, canPrint, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const print = () => { flushSync(() => setOpen(true)); printOne(id); };
  return (
    <section className="ui-section" id={id} data-printable="" aria-labelledby={`${id}-h`}>
      <div className="ui-section__head ui-section__head--center">
        <h2 className="ui-section__title" id={`${id}-h`}>
          <button type="button" className="ui-disclosure" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen(o => !o)}>
            <Icon icon={ChevronDown} />{title}
          </button>
        </h2>
        {meta && <span className="ui-meta">{meta}</span>}
        <div className="ui-section__actions ui-print-hide">
          {onExport && <Button variant="quiet" icon={Download} onClick={onExport}>CSV</Button>}
          {canPrint && <Button variant="quiet" icon={Printer} onClick={print}>Print</Button>}
        </div>
      </div>
      {open && <div id={bodyId}>{children}</div>}
    </section>
  );
}

function Totals({ items }) {
  return (
    <dl className={`ui-kv ui-kv--${items.length > 4 ? 3 : 2} ui-region ui-region--pad`}>
      {items.map(([label, value, note]) => (
        <Fragment key={label}>
          <dt>{label}</dt>
          <dd className="ui-num">{value}{note && <span className="ui-text-3">, {note}</span>}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

const PRESETS = [['7d', 7], ['30d', 30], ['90d', 90]];

export default function Reports() {
  const { hasRole, user } = useAuth();
  const canPrint       = hasRole('Admin', 'Manager', 'Power User');
  const canSeeBilling  = hasRole('Admin', 'Manager', 'Power User');
  const isWriterOnly   = hasRole('Writer') && !hasRole('Admin', 'Manager', 'Power User');

  const [scripts,     setScripts]     = useState([]);
  const [liveAll,     setLiveAll]     = useState([]);
  const [aiAll,       setAiAll]       = useState([]);
  const [clients,     setClients]     = useState([]);
  const [editorStats, setEditorStats] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  // Filters
  const [clientFilter,      setClientFilter]      = useState('all');
  const [dateStart,         setDateStart]         = useState('');
  const [dateEnd,           setDateEnd]           = useState('');
  const [preset,            setPresetName]        = useState(null); // which preset set the range, for aria-pressed
  const [selectedWriterId,  setSelectedWriterId]  = useState('');

  // Table sort states
  const [clientSort,  setClientSort]  = useState({ col: 'scriptsCount',      dir: 'desc' });
  const [writerSort,  setWriterSort]  = useState({ col: 'total',             dir: 'desc' });
  const [billingSort, setBillingSort] = useState({ col: 'dateEntered',       dir: 'desc' });
  const [editorSort,  setEditorSort]  = useState({ col: 'avgTurnaroundDays', dir: 'asc'  });

  // Output (OUT-2): its own data, loading and errors, so the rest of the page is unchanged.
  const toast = useToast();
  const [outRange, setOutRange] = useOutputRange(user?.id);
  const [outPerson, setOutPerson] = useState('');
  const output = useOutput({ range: outRange, from: dateStart, to: dateEnd, person: outPerson, user });
  const outOptions = useMemo(() => personOptions(output.base || output.prevBase), [output.base, output.prevBase]);   // HD-11.1: the names stay while a new range loads
  const outPersonName = outPerson ? outOptions.find(o => o.value === outPerson)?.label : null;
  const exportOutput = () => downloadOutputCsv(outRange, dateStart, dateEnd)
    .catch(() => toast('The Output CSV did not download. Try again.', { tone: 'danger' }));

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      // includeInProduction: Reports COUNTS work, so it must keep scripts whose
      // video card has started shooting. The Scripts index hides those behind
      // its Filming pill, but hiding them here would deflate every writer and
      // client total. Mirrors the excludePosted=false intent on the rows below.
      api.get('/api/scripts?includeInProduction=true'),
      api.get('/api/live-production?excludePosted=false'),
      api.get('/api/ai-production?excludePosted=false'),
      api.get('/api/clients'),
      api.get('/api/live-production/editor-stats'),
    ])
      .then(([s, lv, ai, cl, es]) => {
        setScripts(s.data);
        setLiveAll(lv.data);
        setAiAll(ai.data);
        setClients(cl.data);
        setEditorStats(es.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('The report data did not load. Check your connection and try again.');
        setLoading(false);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  function setPreset(label, days) {
    const end   = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setDateStart(start.toISOString().slice(0, 10));
    setDateEnd(end.toISOString().slice(0, 10));
    setPresetName(label);
  }

  function setThisMonth() {
    const now   = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    setDateStart(start.toISOString().slice(0, 10));
    setDateEnd(now.toISOString().slice(0, 10));
    setPresetName('month');
  }

  function clearDates() {
    setDateStart('');
    setDateEnd('');
    setPresetName(null);
  }

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filteredScripts = useMemo(() => {
    let r = clientFilter === 'all' ? scripts : scripts.filter(s => s.clientId === clientFilter);
    if (dateStart || dateEnd) r = r.filter(s => inDateRange(s.dateEntered, dateStart, dateEnd));
    return r;
  }, [scripts, clientFilter, dateStart, dateEnd]);

  const filteredLive = useMemo(() =>
    clientFilter === 'all' ? liveAll : liveAll.filter(v => v.clientId === clientFilter),
    [liveAll, clientFilter]);

  const filteredAi = useMemo(() =>
    clientFilter === 'all' ? aiAll : aiAll.filter(v => v.clientId === clientFilter),
    [aiAll, clientFilter]);

  // ── Summary counts ────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const totalScripts    = filteredScripts.length;
    const approvedScripts = filteredScripts.filter(s => s.qualStatus === 'Approved').length;
    const liveVideos      = filteredLive.length;
    const aiVideos        = filteredAi.length;
    const livePosted      = filteredLive.filter(v => v.status === 'Posted').length;
    const aiPosted        = filteredAi.filter(v  => v.status === 'Posted').length;
    const scriptsThisMonth = filteredScripts.filter(
      s => thisMonth(s.dateEntered) || thisMonth(s.approvedAt)
    ).length;
    return { totalScripts, approvedScripts, liveVideos, aiVideos, livePosted, aiPosted, scriptsThisMonth };
  }, [filteredScripts, filteredLive, filteredAi]);

  const qualCounts = useMemo(() => buildCounts(filteredScripts, 'qualStatus', QUAL_STATUSES), [filteredScripts]);
  const liveCounts = useMemo(() => buildCounts(filteredLive,    'status',     LIVE_STATUSES), [filteredLive]);
  const aiCounts   = useMemo(() => buildCounts(filteredAi,      'status',     AI_STATUSES),   [filteredAi]);

  // ── Output by project rows (project filter only; not date-filtered, as before) ──
  const clientRows = useMemo(() => {
    const activeClients = clientFilter === 'all'
      ? clients.filter(c => c.isActive)
      : clients.filter(c => c.isActive && c.id === clientFilter);
    const rows = activeClients.map(client => {
      const cScripts = scripts.filter(s => s.clientId === client.id);
      const cLive    = liveAll.filter(v => v.clientId === client.id);
      const cAi      = aiAll.filter(v  => v.clientId === client.id);
      return {
        clientName:    client.name,
        initials:      client.initials,
        clientId:      client.id,
        scriptsCount:  cScripts.length,
        approvedCount: cScripts.filter(s => s.qualStatus === 'Approved').length,
        liveVideos:    cLive.length,
        aiVideos:      cAi.length,
        livePosted:    cLive.filter(v => v.status === 'Posted').length,
        aiPosted:      cAi.filter(v  => v.status === 'Posted').length,
      };
    });
    return applySortRows(rows, clientSort);
  }, [clients, scripts, liveAll, aiAll, clientFilter, clientSort]);

  // ── Scripts by writer rows ────────────────────────────────────────────────
  const writerRows = useMemo(() => {
    const map = {};
    filteredScripts.forEach(s => {
      const id   = s.writerId;
      const name = s.writer?.name || 'Unknown';
      if (!map[id]) map[id] = { writerName: name, total: 0, approved: 0 };
      map[id].total++;
      if (s.qualStatus === 'Approved') map[id].approved++;
    });
    const rows = Object.values(map).map(r => ({ ...r, approvalPct: r.total > 0 ? Math.round((r.approved / r.total) * 100) : 0 }));
    return applySortRows(rows, writerSort);
  }, [filteredScripts, writerSort]);

  // ── Scripts written (billing) rows ────────────────────────────────────────
  // Format labeling: 'Recipe' rows are Cooking Phase 1. 'TH' rows linked to a Phase 2 live_production
  // (isCookingPhase2 from the API) are relabeled so the writer-paid-per-script billing view doesn't undercount the
  // cooking workflow. The CSV keeps the label it always had; the screen shows it without the dash.
  const billingRows = useMemo(() => {
    const base = canSeeBilling ? filteredScripts : (isWriterOnly ? filteredScripts.filter(s => s.writerId === user?.id) : []);
    const rows = base.map(s => ({
      id:          s.id,
      writerName:  s.writer?.name || DASH,
      clientName:  s.client?.name || DASH,
      scriptName:  s.scriptName,
      format:      s.isCookingPhase2 ? 'TH (Phase 2 — Cooking)' : (s.format || DASH),
      status:      s.qualStatus,
      dateEntered: s.dateEntered ? s.dateEntered.slice(0, 10) : DASH,
    }));
    return applySortRows(rows, billingSort);
  }, [filteredScripts, billingSort, canSeeBilling, isWriterOnly, user]);

  // ── Writer report data ────────────────────────────────────────────────────
  const writerMembers = useMemo(() => {
    const map = {};
    scripts.forEach(s => {
      if (s.writerId && s.writer?.name) map[s.writerId] = s.writer.name;
    });
    return Object.entries(map)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [scripts]);

  const writerReportId = isWriterOnly ? user?.id : selectedWriterId;

  const writerReportScripts = useMemo(() =>
    writerReportId ? scripts.filter(s => s.writerId === writerReportId) : [],
    [scripts, writerReportId]);

  const writerReportName = useMemo(() => {
    if (!writerReportId) return '';
    return scripts.find(s => s.writerId === writerReportId)?.writer?.name || '';
  }, [scripts, writerReportId]);

  const writerStats = useMemo(() => ({
    total:      writerReportScripts.length,
    approved:   writerReportScripts.filter(s => s.qualStatus === 'Approved').length,
    inQual:     writerReportScripts.filter(s => ['I/P', 'Needs Review', 'Needs Revision'].includes(s.qualStatus)).length,
    backburner: writerReportScripts.filter(s => s.qualStatus === 'Backburner').length,
  }), [writerReportScripts]);

  const sortedEditorStats = useMemo(() => applySortRows(editorStats, editorSort), [editorStats, editorSort]);

  // ── Context line for the overview and the billing count ───────────────────
  const selectedClientName = useMemo(() =>
    clientFilter === 'all' ? null : clients.find(c => c.id === clientFilter)?.name || null,
    [clientFilter, clients]);

  const dateLabel = dateStart && dateEnd ? `${dateStart} to ${dateEnd}`
    : dateStart ? `from ${dateStart}`
    : dateEnd ? `until ${dateEnd}`
    : null;

  // ── CSV exports (columns and file names unchanged) ────────────────────────
  function exportClientRows() {
    downloadCSV(clientRows.map(r => ({
      Client: r.clientName, Scripts: r.scriptsCount, Approved: r.approvedCount,
      'Live Videos': r.liveVideos, 'AI Videos': r.aiVideos,
      'Live Posted': r.livePosted, 'AI Posted': r.aiPosted,
    })), 'output-by-project.csv');
  }

  function exportWriterRows() {
    downloadCSV(writerRows.map(r => ({
      Writer: r.writerName, 'Scripts Written': r.total, Approved: r.approved, 'Approval %': `${r.approvalPct}%`,
    })), 'scripts-by-writer.csv');
  }

  function exportBillingRows() {
    downloadCSV(billingRows.map(r => ({
      Writer: r.writerName, Client: r.clientName, Script: r.scriptName, Format: r.format, Status: r.status, Date: r.dateEntered,
    })), 'scripts-written-billing.csv');
  }

  function exportEditorStats() {
    downloadCSV(sortedEditorStats.map(r => ({
      Editor: r.name,
      'Avg Turnaround (days)': r.avgTurnaroundDays.toFixed(1),
      'Revision Rate %': `${r.revisionRate}%`,
      'Current Queue': r.queueDepth,
    })), 'editor-performance.csv');
  }

  const writerDetailCSV = () => writerReportScripts.map(s => ({
    Script: s.scriptName,
    Client: s.client?.name || DASH,
    Status: s.qualStatus,
    Date:   s.dateEntered ? s.dateEntered.slice(0, 10) : DASH,
  }));

  function exportWriterDetail() {
    downloadCSV(writerDetailCSV(), `writer-report-${writerReportName.replace(/\s+/g, '-').toLowerCase() || 'unknown'}.csv`);
  }

  // ── Header ────────────────────────────────────────────────────────────────
  const activeClients = clients.filter(c => c.isActive);
  const datesSet = !!(dateStart || dateEnd);
  usePageHeader({
    title: 'Reports',
    meta: 'Output by project, writer and editor',
    actions: canPrint ? <Button icon={Printer} onClick={() => window.print()} disabled={loading}>Print all</Button> : null,
    filters: (
      <>
        <Select aria-label="Project" value={clientFilter} onChange={e => setClientFilter(e.target.value)}
          options={[{ value: 'all', label: 'All projects' }, ...activeClients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
        <DatePicker aria-label="From date" value={dateStart} max={dateEnd || undefined}
          onChange={e => { setDateStart(e.target.value); setPresetName(null); }} />
        <span className="ui-text-3 ui-dense">to</span>
        <DatePicker aria-label="To date" value={dateEnd} min={dateStart || undefined}
          onChange={e => { setDateEnd(e.target.value); setPresetName(null); }} />
        <div className="ui-seg" role="group" aria-label="Date range presets">
          {PRESETS.map(([label, days]) => (
            <Button key={label} variant="quiet" aria-pressed={preset === label} onClick={() => setPreset(label, days)}>{label}</Button>
          ))}
          <Button variant="quiet" aria-pressed={preset === 'month'} onClick={setThisMonth}>This month</Button>
        </div>
        {datesSet && <Button variant="quiet" icon={X} onClick={clearDates}>Clear dates</Button>}
        <OutputFilters range={outRange} setRange={setOutRange} person={outPerson} setPerson={setOutPerson} options={outOptions} scope={output.scope} />
      </>
    ),
  });

  const outputSection = (
    <Section id="report-output" title="Output" canPrint={canPrint} onExport={canPrint && output.scope === 'everyone' && output.state === 'data' ? exportOutput : null}
      meta={[output.base ? periodLabel(output.base.range) : null, outPersonName ? clean(outPersonName) : null].filter(Boolean).join(', ') || 'Scripted, filmed and edited minutes against daily targets'}>
      <OutputBody out={output} user={user} clientFilter={clientFilter} person={outPerson} />
    </Section>
  );

  if (loading) {
    return (
      <div className="ui ui-page">
        {outputSection}
        <div className="ui-region ui-region--pad ui-stack"><Skeleton width="30%" /><Skeleton width="60%" /><Skeleton width="45%" /></div>
        <div className="ui-cols ui-cols--3">
          {[0, 1, 2].map(i => <Skeleton key={i} variant="block" height={200} />)}
        </div>
        <Table columns={[{ key: 'a', label: 'Project' }, { key: 'b', label: 'Scripts' }, { key: 'c', label: 'Approved' }]} rows={[]} state="loading" />
      </div>
    );
  }

  if (error) {
    return <div className="ui ui-page">{outputSection}<ErrorState title="Reports did not load" message={error} onRetry={load} /></div>;
  }

  // ── Table columns ─────────────────────────────────────────────────────────
  const num = key => r => <span className="ui-num">{r[key]}</span>;
  const clientCols = sortable([
    { key: 'clientName', label: 'Project', width: '28%', render: r => <span className="ui-cluster">{r.initials && <Tag>{r.initials}</Tag>}{clean(r.clientName)}</span> },
    { key: 'scriptsCount', label: 'Scripts', align: 'right', render: num('scriptsCount') },
    { key: 'approvedCount', label: 'Approved', align: 'right', render: num('approvedCount') },
    { key: 'liveVideos', label: 'Live videos', align: 'right', render: num('liveVideos') },
    { key: 'aiVideos', label: 'AI videos', align: 'right', render: num('aiVideos') },
    { key: 'livePosted', label: 'Live posted', align: 'right', render: num('livePosted') },
    { key: 'aiPosted', label: 'AI posted', align: 'right', render: num('aiPosted') },
  ], clientSort, setClientSort);

  const billingCols = sortable([
    { key: 'writerName', label: 'Writer', width: '16%', render: r => orNone(r.writerName) },
    { key: 'clientName', label: 'Project', width: '16%', render: r => orNone(r.clientName) },
    { key: 'scriptName', label: 'Script', width: '28%', render: r => <span title={r.scriptName}>{clean(r.scriptName)}</span> },
    { key: 'format', label: 'Format', render: r => orNone(r.format) },
    { key: 'status', label: 'Status', render: r => <QualStatus value={r.status} /> },
    { key: 'dateEntered', label: 'Date', align: 'right', render: r => (r.dateEntered === DASH ? orNone(DASH) : <span className="ui-num">{r.dateEntered}</span>) },
  ], billingSort, setBillingSort);

  const writerCols = sortable([
    { key: 'writerName', label: 'Writer', width: '40%', render: r => clean(r.writerName) },
    { key: 'total', label: 'Scripts written', align: 'right', render: num('total') },
    { key: 'approved', label: 'Approved', align: 'right', render: num('approved') },
    { key: 'approvalPct', label: 'Approval rate', align: 'right', render: r => <span className="ui-num">{r.approvalPct}%</span> },
  ], writerSort, setWriterSort);

  // The old colour thresholds become badges: revision rate over 30% danger, over 15% warn; queue over 5 danger.
  const editorCols = sortable([
    { key: 'name', label: 'Editor', width: '34%', render: r => clean(r.name) },
    { key: 'avgTurnaroundDays', label: 'Average turnaround (days)', align: 'right',
      render: r => (r.avgTurnaroundDays > 0 ? <span className="ui-num">{r.avgTurnaroundDays.toFixed(1)}</span> : orNone(null)) },
    { key: 'revisionRate', label: 'Revision rate', align: 'right', render: r => {
      if (!(r.totalQuald > 0)) return orNone(null);
      const text = <span className="ui-num">{r.revisionRate}%</span>;
      if (r.revisionRate > 30) return <Badge tone="danger">{text}</Badge>;
      if (r.revisionRate > 15) return <Badge tone="warn">{text}</Badge>;
      return text;
    } },
    { key: 'queueDepth', label: 'Queue depth', align: 'right',
      render: r => (r.queueDepth > 5 ? <Badge tone="danger"><span className="ui-num">{r.queueDepth}</span></Badge> : <span className="ui-num">{r.queueDepth}</span>) },
  ], editorSort, setEditorSort);

  const writerTotals = (withName) => (
    <Totals items={[
      ['Scripts written', writerStats.total, withName ? clean(writerReportName) : null],
      ['Approved', writerStats.approved],
      ['In qual', writerStats.inQual],
      ['Backburner', writerStats.backburner],
    ]} />
  );

  const overviewNote = [selectedClientName ? clean(selectedClientName) : 'All projects', dateLabel ? `scripts entered ${dateLabel}` : 'all dates'].join(', ');

  return (
    <div className="ui ui-page">

      {outputSection}

      <section className="ui-section" id="report-overview" data-printable="" aria-labelledby="report-overview-h">
        <div className="ui-section__head">
          <h2 className="ui-section__title" id="report-overview-h">Overview</h2>
        </div>
        <p className="ui-section__note">{overviewNote}. Video counts follow the project filter only.</p>
        <Totals items={[
          ['Scripts', summary.totalScripts, `${summary.scriptsThisMonth} this month`],
          ['Approved scripts', summary.approvedScripts],
          ['Live videos', summary.liveVideos],
          ['AI videos', summary.aiVideos],
          ['Live posted', summary.livePosted],
          ['AI posted', summary.aiPosted],
        ]} />
      </section>

      <div className="ui-cols ui-cols--3">
        <Section id="report-scripts" title="Scripts by status" meta={`${summary.totalScripts} total`} canPrint={canPrint}>
          <div className="ui-region ui-region--pad"><Bars statuses={QUAL_STATUSES} counts={qualCounts} labelOf={qualLabel} toneOf={qualTone} /></div>
        </Section>
        <Section id="report-live" title="Live production" meta={`${summary.liveVideos} total`} canPrint={canPrint}>
          <div className="ui-region ui-region--pad"><Bars statuses={LIVE_STATUSES} counts={liveCounts} labelOf={stageLabel} toneOf={stageTone} /></div>
        </Section>
        <Section id="report-ai" title="AI production" meta={`${summary.aiVideos} total`} canPrint={canPrint}>
          <div className="ui-region ui-region--pad"><Bars statuses={AI_STATUSES} counts={aiCounts} labelOf={stageLabel} toneOf={stageTone} /></div>
        </Section>
      </div>

      <Section id="report-projects" title="Output by project" meta={plural(clientRows.length, 'active project')}
        canPrint={canPrint} onExport={canPrint ? exportClientRows : null}>
        <div className="ui-region">
          <Table caption="Output by project" columns={clientCols} rows={clientRows} rowKey={r => r.clientId}
            empty={{ message: 'No active projects match the project filter. Choose All projects to see every one.' }} />
        </div>
      </Section>

      {canSeeBilling && (
        <Section id="report-billing" title="Scripts written" defaultOpen={false} canPrint onExport={exportBillingRows}
          meta={`${plural(billingRows.length, 'script')}${dateLabel ? `, ${dateLabel}` : ''}`}>
          <div className="ui-region">
            <Table caption="Scripts written" columns={billingCols} rows={billingRows}
              empty={{ message: 'No scripts were entered in this range. Widen the dates or clear them.' }} />
          </div>
        </Section>
      )}

      {!isWriterOnly && (
        <Section id="report-writers" title="Scripts by writer" meta={plural(writerRows.length, 'writer')}
          canPrint={canPrint} onExport={canPrint ? exportWriterRows : null}>
          <div className="ui-region">
            <Table caption="Scripts by writer" columns={writerCols} rows={writerRows} rowKey={r => r.writerName}
              empty={{ message: 'No scripts match these filters. Widen the dates or choose All projects.' }} />
          </div>
        </Section>
      )}

      {!isWriterOnly && (
        <Section id="report-writer-detail" title="Writer report" meta="A writer's full script list" defaultOpen={false}
          onExport={selectedWriterId ? exportWriterDetail : null} canPrint={!!selectedWriterId && canPrint}>
          <div className="ui-stack">
            <Select aria-label="Writer" className="ui-fit" value={selectedWriterId} onChange={e => setSelectedWriterId(e.target.value)}
              placeholder="Choose a writer" options={writerMembers.map(w => ({ value: w.id, label: clean(w.name) }))} />
            {selectedWriterId
              ? <>{writerTotals(true)}<WriterScriptTable scripts={writerReportScripts} clients={clients} /></>
              : <div className="ui-region"><EmptyState message="Choose a writer to see every script they have written." /></div>}
          </div>
        </Section>
      )}

      {isWriterOnly && (
        <Section id="report-my-scripts" title="My scripts" meta={`${writerStats.total} total`} canPrint
          onExport={() => downloadCSV(writerDetailCSV(), 'my-scripts.csv')}>
          <div className="ui-stack">
            {writerTotals(false)}
            <WriterScriptTable scripts={writerReportScripts} clients={clients} />
          </div>
        </Section>
      )}

      {!isWriterOnly && (
        <Section id="report-editors" title="Editor performance" meta={plural(sortedEditorStats.length, 'editor')} defaultOpen={false}
          canPrint={canPrint} onExport={canPrint ? exportEditorStats : null}>
          <div className="ui-region">
            <Table caption="Editor performance" columns={editorCols} rows={sortedEditorStats}
              empty={{ message: 'No editor has a cut in the pipeline yet.' }} />
          </div>
        </Section>
      )}

    </div>
  );
}

// ── Writer script list ───────────────────────────────────────────────────────

function WriterScriptTable({ scripts: list, clients }) {
  const [sort, setSort] = useState({ col: 'dateEntered', dir: 'desc' });

  const sorted = useMemo(() => {
    if (!sort.col) return list;
    return [...list].sort((a, b) => {
      let av = '', bv = '';
      if      (sort.col === 'scriptName')  { av = a.scriptName || ''; bv = b.scriptName || ''; }
      else if (sort.col === 'clientName')  { av = a.client?.name || ''; bv = b.client?.name || ''; }
      else if (sort.col === 'qualStatus')  { av = a.qualStatus || ''; bv = b.qualStatus || ''; }
      else if (sort.col === 'dateEntered') { av = a.dateEntered || ''; bv = b.dateEntered || ''; }
      const cmp = av.localeCompare(bv);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [list, sort]);

  const cols = sortable([
    { key: 'scriptName', label: 'Script', width: '40%', render: s => <span title={s.scriptName}>{clean(s.scriptName)}</span> },
    { key: 'clientName', label: 'Project', render: s => orNone(s.client?.name || clients.find(c => c.id === s.clientId)?.name) },
    { key: 'qualStatus', label: 'Status', render: s => <QualStatus value={s.qualStatus} /> },
    { key: 'dateEntered', label: 'Date', align: 'right', render: s => (s.dateEntered
      ? <span className="ui-num">{new Date(s.dateEntered).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
      : orNone(null)) },
  ], sort, setSort);

  return (
    <div className="ui-region">
      <Table caption="Scripts by this writer" columns={cols} rows={sorted} empty={{ message: 'This writer has no scripts yet.' }} />
    </div>
  );
}
